import { AnswerType, GameSessionType, PlayerType, QuestionType } from '../types/entities';

declare global {
  interface Window {
    dataLayer?: {
      push: (data: Record<string, string | number>) => void;
    };
  }
}

type AnalyticsPlayerType = {
  username: string;
  soe_id: string;
};

type AnalyticsTimeoutType = AnalyticsPlayerType & {
  event: 'timed_out';
  question_text: string;
  // difficulty: string;
  category: string;
  game_session_id: string;
  city: string;
};

type AnalyticsQuestionShownType = Omit<AnalyticsTimeoutType, 'event'> & {
  event: 'questions';
  answer_text: string;
  answer_result: string;
};

type AnalyticsQuestionAnsweredType = AnalyticsQuestionShownType;

type AnalyticsResultType = AnalyticsPlayerType & {
  event: 'result';
  final_score: number;
  questions_right: number;
  surveys_answered: number;
  game_session_id: string;
};

const onQuestionShown = (data: {
  player: PlayerType;
  gameSession: GameSessionType;
  question: QuestionType;
}) => {
  const { question, gameSession, player } = data;
  const params: AnalyticsQuestionShownType = {
    event: 'questions',
    username: player.first_name + ' ' + player.last_name,
    soe_id: player.soeid,
    question_text: question.content,
    category: question.category,
    game_session_id: gameSession.documentId,
    city: gameSession.city || '',
    answer_text: '',
    answer_result: '',
  };

  window.dataLayer?.push(params);
};

const onQuestionAnswered = (data: {
  player: PlayerType;
  gameSession: GameSessionType;
  question: QuestionType;
  answer: AnswerType;
}) => {
  const { question, gameSession, answer, player } = data;
  const isAnswerCorrect =
    gameSession?.questionsAnswered.find(
      (questionAnswered) => questionAnswered.question.documentId === question.documentId,
    )?.correctAnswerId === answer.id;
  const params: AnalyticsQuestionAnsweredType = {
    event: 'questions',
    username: player.first_name + ' ' + player.last_name,
    soe_id: player.soeid,
    question_text: question.content,
    category: question.category,
    game_session_id: gameSession.documentId,
    city: gameSession.city || '',
    answer_text: answer.content,
    answer_result: isAnswerCorrect ? 'correct' : 'wrong',
  };

  window.dataLayer?.push(params);
};

const onTimeout = (data: {
  player: PlayerType;
  gameSession: GameSessionType;
  question: QuestionType;
}) => {
  const { question, gameSession, player } = data;
  const params: AnalyticsTimeoutType = {
    event: 'timed_out',
    username: player.first_name + ' ' + player.last_name,
    soe_id: player.soeid,
    question_text: question.content,
    category: question.category,
    city: gameSession.city || '',
    game_session_id: gameSession.documentId,
  };

  window.dataLayer?.push(params);
};

const onResult = (data: { player: PlayerType; gameSession: GameSessionType }) => {
  const { gameSession, player } = data;
  const params: AnalyticsResultType = {
    event: 'result',
    username: player.first_name + ' ' + player.last_name,
    soe_id: player.soeid,
    final_score: gameSession.points || 0,
    questions_right:
      gameSession.questionsAnswered.filter(
        (questionAnswered) =>
          questionAnswered.question?.category !== 'Survey' &&
          questionAnswered.correctAnswerId === questionAnswered.answerId,
      ).length || 0,
    surveys_answered:
      gameSession.questionsAnswered.filter(
        (questionAnswered) =>
          questionAnswered.question?.category === 'Survey' && questionAnswered.answerId != null,
      ).length || 0,
    game_session_id: gameSession.documentId,
  };

  window.dataLayer?.push(params);
};

export const analytics = {
  onQuestionShown,
  onQuestionAnswered,
  onTimeout,
  onResult,
};
